import { useMemo, useState } from "react";

import { ApiError, api, type Heat, type MediaTarget, type Tournament } from "../api";
import { useAction, useLoad } from "../useLoad";
import { Leaderboard } from "./Leaderboard";
import { MediaSheet } from "./MediaSheet";
import { Picks } from "./Picks";
import { PickedBy, backersOf } from "./picked";

interface Props {
  teamId: string;
  mySub: string;
  tournamentId: string;
  revision: number;
  onError: (message: string) => void;
  onClose: () => void;
}

/**
 * Running a tournament from the phone. Task 8.6, requirement 14.5 — the web app has **full control**.
 *
 * Everything here goes through the same endpoints the television uses, which is what makes the two
 * genuinely interchangeable rather than one being a remote for the other. It also means the two can
 * disagree, and requirement 6.6 settles that: a heat result is set-once and keyed by heat id, first
 * write wins, and the loser is told someone else already called the race.
 */
export function TournamentControl({ teamId, mySub, tournamentId, revision, onError, onClose }: Props) {
  const { data, loading, reload } = useLoad(
    () => api.tournament(teamId, tournamentId),
    [teamId, tournamentId, revision],
    onError,
  );
  const { busy, run } = useAction(onError);
  // Requirement 9.9 needs an avatar per member, and the leaderboard needs their names, so the roster is
  // loaded alongside the bracket rather than resolved per pick.
  const roster = useLoad(() => api.members(teamId), [teamId, revision], onError);
  const [order, setOrder] = useState<string[]>([]);
  /** Whether the race-calling panel is open. Closed by default — see the panel's own comment. */
  const [calling, setCalling] = useState(false);
  /** The full finishing order awaiting confirmation. Requirement 4.2's podium. */
  const [reviewing, setReviewing] = useState<string[] | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [mediaFor, setMediaFor] = useState<MediaTarget | "tournament" | null>(null);

  const tournament = data;
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const entrant of tournament?.entrants ?? []) {
      map.set(entrant.vehicleId, entrant.name ?? "—");
    }
    return map;
  }, [tournament]);

  if (!tournament) {
    return (
      <div className="centre">
        {loading ? <div className="spinner" /> : <p className="muted">Couldn't load that tournament.</p>}
        <button className="btn" onClick={onClose}>
          Back
        </button>
      </div>
    );
  }

  const next = tournament.nextHeat
    ? tournament.heats.find(
        (heat) => heat.round === tournament.nextHeat?.round && heat.index === tournament.nextHeat?.index,
      )
    : undefined;

  // At 2-wide picking the winner decides the heat; at 4-wide the last place is whatever is left, so
  // width - 1 selections complete the order. Requirement 2.6 records the full finishing order either way.
  const needed = Math.max(1, tournament.width - 1);
  // How many of the podium go through, so the confirmation can say which. Halving per round (2.2) means
  // this is half the field at 4-wide and the winner alone at 2-wide.
  const advancing = Math.max(1, Math.floor(tournament.width / 2));

  const commit = (heat: Heat, finishingOrder: string[]) => {
    setConflict(null);
    void run(
      async () => {
        try {
          await api.commitHeat(teamId, tournamentId, heat.round, heat.index, finishingOrder);
        } catch (cause) {
          if (cause instanceof ApiError && cause.isConflict) {
            // Requirement 6.6. Not an error to apologise for — the television or another phone got
            // there first, and the right response is to show what actually happened.
            setConflict("Someone else already called this race.");
            return;
          }
          throw cause;
        }
      },
      () => {
        setOrder([]);
        setReviewing(null);
        // Closed again on purpose. Calling one race should not leave the controls armed over the next
        // one, which is how a single deliberate act turns into an accidental second.
        setCalling(false);
        reload();
      },
    );
  };

  const complete = tournament.state === "complete";

  return (
    <>
      <div className="card">
        <div className="row">
          <span className="grow">
            <span className="name">{tournament.name ?? "Grayson Earl Jam"}</span>
            <div className="muted">
              {tournament.commitLog.length} of {tournament.totalHeatCount} races ·{" "}
              {tournament.size} racers · {tournament.width} at a time
            </div>
          </span>
          <button className="btn small" onClick={onClose}>
            Back
          </button>
        </div>
      </div>

      {conflict && <div className="banner error">{conflict}</div>}

      {complete && (
        <div className="card">
          <p className="section-title">Champion</p>
          <p style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>
            {tournament.champion ? names.get(tournament.champion) ?? "—" : "—"}
          </p>
        </div>
      )}

      {!complete && next && (
        <div className="card">
          <p className="section-title">
            {tournament.roundCount === next.round ? "The final" : `Round ${next.round}`} · race{" "}
            {next.index + 1}
          </p>

          {/*
            Requirement 14.5 gives the phone full control, including advancing races — but with "the
            tvOS app as the primary basic control surface". This panel is collapsed to honour the second
            half of that sentence.

            It used to be open, sitting above the predictions on this same screen, captioned "Tap the
            winner." A member opening a live tournament to get their pick in met the result-committing
            controls first and the prediction form further down, and calling a race by accident while
            trying to predict was not a mistake so much as the layout's suggestion. Requirement 4.4
            makes the only correction undo-last, so an accident two races later is unrecoverable.
          */}
          {!calling ? (
            <>
              <p className="muted" style={{ marginBottom: 10 }}>
                {next.participants.map((id) => names.get(id) ?? "—").join(" v ")}
              </p>
              <button className="btn wide" onClick={() => setCalling(true)}>
                Call this race
              </button>
              <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
                Normally the TV calls it. Use this if the remote has wandered off.
              </p>
            </>
          ) : (
            <>
              <p className="muted" style={{ marginBottom: 10 }}>
                {needed === 1
                  ? "Tap the winner."
                  : `Tap them in order, first to ${needed === 2 ? "second" : `${needed}th`}.`}
              </p>
              {next.participants.map((vehicleId) => {
                const at = order.indexOf(vehicleId);
                return (
                  <button
                    key={vehicleId}
                    className={`lane${at >= 0 ? " picked" : ""}`}
                    disabled={busy}
                    onClick={() =>
                      setOrder((current) =>
                        current.includes(vehicleId)
                          ? current.filter((entry) => entry !== vehicleId)
                          : current.length < needed
                            ? [...current, vehicleId]
                            : current,
                      )
                    }
                  >
                    <span className="place">{at >= 0 ? at + 1 : ""}</span>
                    <span className="grow name">{names.get(vehicleId) ?? "—"}</span>
                    <PickedBy
                      subs={backersOf(tournament, next, vehicleId)}
                      members={roster.data?.members ?? []}
                    />
                  </button>
                );
              })}
              <div className="inline-form" style={{ marginTop: 12 }}>
                <button
                  className="btn primary grow"
                  style={{ flex: 1 }}
                  disabled={busy || order.length < needed}
                  onClick={() => setReviewing(podiumFor(next, order))}
                >
                  {order.length < needed ? `Pick ${needed - order.length} more` : "Review the result"}
                </button>
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => {
                    setOrder([]);
                    setCalling(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/*
        Requirement 4.2 — committing a result requires an explicit confirmation showing the resulting
        podium, and 4.3 extends that to "every irreversible action". The television has had this since
        Milestone 2; the phone committed on a single tap with nothing in between, which is the more
        dangerous of the two surfaces because it lives in a pocket.

        Cancel comes first and is the plain button, per 4.3's rule that the answer offered first is no.
      */}
      {reviewing && next && (
        <div className="sheet" role="dialog" aria-modal="true" aria-label="Confirm this result">
          <div className="sheet-inner">
            <p className="section-title">
              {tournament.roundCount === next.round ? "The final" : `Round ${next.round}`} · race{" "}
              {next.index + 1}
            </p>
            <p className="muted" style={{ marginBottom: 10 }}>
              This is what gets recorded. Only the last race can be undone.
            </p>
            {reviewing.map((vehicleId, at) => (
              <div key={vehicleId} className={`lane${at < advancing ? " picked" : ""}`}>
                <span className="place">{at + 1}</span>
                <span className="grow name">{names.get(vehicleId) ?? "—"}</span>
                <span className="muted">{at < advancing ? "through" : "out"}</span>
              </div>
            ))}
            <div className="inline-form" style={{ marginTop: 14 }}>
              <button className="btn grow" style={{ flex: 1 }} disabled={busy} onClick={() => setReviewing(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={busy}
                onClick={() => commit(next, reviewing)}
              >
                {busy ? "Saving…" : "Call it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!complete && !next && (
        <div className="banner info">
          Every race that can run has run. The next one is waiting on a result upstream.
        </div>
      )}

      {tournament.commitLog.length > 0 && (
        <div className="card">
          <div className="row">
            <p className="section-title grow" style={{ margin: 0 }}>
              Undo
            </p>
            {/*
              Requirement 4.4 — undo-last only, and deliberately no reset-from-here. The accepted
              consequence is that a bracket wrong two rounds deep can only be abandoned, which is why
              this is the one destructive control on the screen and it names what it will undo.
            */}
            <button
              className="btn small danger"
              disabled={busy}
              onClick={() => void run(() => api.undoLast(teamId, tournamentId), reload)}
            >
              Undo last race
            </button>
          </div>
        </div>
      )}

      {/* Milestone 11. Above the bracket because during a live tournament the thing a member most wants
          from their phone is to get their pick in before the next race starts. */}
      <Picks
        teamId={teamId}
        tournament={tournament}
        members={roster.data?.members ?? []}
        mySub={mySub}
        names={names}
        onChanged={reload}
        onError={onError}
      />

      <Leaderboard
        teamId={teamId}
        tournament={tournament}
        revision={revision}
        onError={onError}
      />

      {/* Requirement 8.2 — the track build, the crowd, the trophy shot. Not of any one race, so it has
          nowhere to live on the bracket and gets a card of its own. */}
      <div className="card">
        <div className="row">
          <p className="section-title grow" style={{ margin: 0 }}>
            Tournament footage
          </p>
          <button className="btn small" onClick={() => setMediaFor("tournament")}>
            Open
          </button>
        </div>
      </div>

      <Bracket
        tournament={tournament}
        names={names}
        onOpenMedia={(heat) => setMediaFor({ round: heat.round, index: heat.index })}
      />

      {mediaFor && (
        <MediaSheet
          teamId={teamId}
          tournament={tournament}
          target={mediaFor}
          names={names}
          onClose={() => setMediaFor(null)}
          onChanged={reload}
          onError={onError}
        />
      )}
    </>
  );
}

/**
 * The full finishing order from a partial selection. Requirement 2.6 records the whole order, and at
 * 4-wide the last place is whatever nobody picked, so the tail is implied rather than tapped.
 */
function podiumFor(heat: Heat, order: string[]): string[] {
  return [...order, ...heat.participants.filter((id) => !order.includes(id))];
}

function Bracket({
  tournament,
  names,
  onOpenMedia,
}: {
  tournament: Tournament;
  names: Map<string, string>;
  onOpenMedia: (heat: Heat) => void;
}) {
  const rounds = Array.from({ length: tournament.roundCount }, (_, index) => index + 1);
  return (
    <div className="card">
      <p className="section-title">Bracket</p>
      {rounds.map((round) => {
        const heats = tournament.heats.filter((heat) => heat.round === round);
        return (
          <div key={round} className="bracket-round" style={{ marginBottom: 12 }}>
            <p className="muted" style={{ margin: 0 }}>
              {round === tournament.roundCount ? "Final" : `Round ${round}`}
            </p>
            {heats.map((heat) => (
              <div key={`${heat.round}-${heat.index}`} className={`heat-chip${heat.state === "complete" ? " done" : ""}`}>
                {heat.finishingOrder ? (
                  <>
                    <span className="winner">{names.get(heat.finishingOrder[0] ?? "") ?? "—"}</span>
                    <span className="muted grow">
                      beat{" "}
                      {heat.finishingOrder
                        .slice(1)
                        .map((id) => names.get(id) ?? "—")
                        .join(", ")}
                    </span>
                  </>
                ) : (
                  <span className="muted grow">
                    {heat.participants.length === 0
                      ? "waiting"
                      : heat.participants.map((id) => names.get(id) ?? "—").join(" v ")}
                  </span>
                )}
                {/*
                  Requirement 8.1, and access pattern M5 is why this is cheap: `mediaCount` is
                  denormalized onto the heat, so the badge comes back with the bracket and costs no
                  extra read. A heat with no field yet has no race to film.
                */}
                {heat.participants.length > 0 && (
                  <button
                    className={`btn small film${heat.mediaCount > 0 ? " has" : ""}`}
                    onClick={() => onOpenMedia(heat)}
                    aria-label={
                      heat.mediaCount > 0
                        ? `${heat.mediaCount} clips for this race`
                        : "Add footage of this race"
                    }
                  >
                    🎬{heat.mediaCount > 0 ? ` ${heat.mediaCount}` : ""}
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
